import { getTextFieldSorter, getDateFieldSorter, getSortOrder } from './sorters';

/**
 * Build an antd Table `columns` config for the standard case-list
 * pattern used across the frontend: every column uses its `key` as
 * its `dataIndex`, every sortable column receives a text or date
 * sorter driven by the active sortOrder/sortBy, and individual
 * columns opt out of sorting either via `sortable: false` on the
 * column itself or the `nonSortableKeys` option.
 *
 * Input columns keep their usual antd shape (`title`, `key`, `render`,
 * `width`, `align`, `onCell`, ...). Explicit `sorter` / `sortOrder` /
 * `dataIndex` on a column are always preserved. The helper never
 * mutates the inputs.
 *
 * @param {Array<object>} columns base column definitions
 * @param {object} [options]
 * @param {string} [options.sortOrder]        current sort direction ('ascend' | 'descend')
 * @param {string} [options.sortBy]           key of the currently-sorted column
 * @param {string[]} [options.dateKeys]       keys that should use a date-aware sorter
 * @param {string[]} [options.nonSortableKeys] keys to skip sorter/sortOrder injection for
 * @returns {Array<object>} fully configured column definitions
 */
export const buildSortableColumns = (columns, options = {}) => {
  const {
    sortOrder,
    sortBy,
    dateKeys = [],
    nonSortableKeys = [],
  } = options;

  return columns.map((column) => {
    const { sortable, ...rest } = column;
    // dataIndex defaults to key; explicit dataIndex in `rest` wins.
    const merged = column.key !== undefined
      ? { dataIndex: column.key, ...rest }
      : { ...rest };

    if (column.key === undefined) {
      return merged;
    }

    const optedOut = sortable === false || nonSortableKeys.includes(column.key);
    if (optedOut) {
      return merged;
    }

    if (merged.sorter === undefined) {
      merged.sorter = dateKeys.includes(column.key)
        ? getDateFieldSorter(column.key)
        : getTextFieldSorter(column.key);
    }
    if (merged.sortOrder === undefined) {
      merged.sortOrder = getSortOrder(sortOrder, sortBy, column.key);
    }

    return merged;
  });
};
